import React, { useRef } from 'react'
import Content from 'blocks.app/content/content'
import Stick from 'blocks.simple/stick/stick'
import ScrollUp from 'blocks.simple/scrollUp/scrollUp'
import ScheduleAddMethods, {
    Broadcast,
    Device,
    IScheduleAddMethodsProps,
    IScheduleFilterItem,
} from './schedule_add.local.methods'
import Button from 'blocks.simple/button/button'
import AddSchedule from 'blocks.app/addSchedule/addSchedule'
import AddScheduleToolbar from 'blocks.app/addSchedule/_toolbar/addSchedule_toolbar'
import helpers from 'core/helpers'
import { connect } from 'react-redux'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { grid, text } from '../../../theme'
import styles from './schedule_add.jcss'
import { routes } from 'features/routes'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import { headActions } from 'features/head/Head.state'
import { checkAvailableModule } from '../../../core/helpers/routes/routes'
import { IAddScheduleRef, IEditableSchedule } from '../../../blocks.app/addSchedule/addSchedule.local.methods'
import { AppDispatch } from '../../../core/store'
import { IRootState } from '../../../core/store/rootReducer'
import { IUserSettingsView } from '../../../core/models/Users'

interface IScheduleAddStateProps {
    selectedDate: number
    filterList: IScheduleFilterItem[]
    view?: IUserSettingsView
}

interface IScheduleAddDispatchProps {
    setTitle: (title: string[]) => void
}

interface IScheduleAddOwnProps {
    location: IScheduleAddMethodsProps['location']
}

type ScheduleAddProps = IScheduleAddStateProps & IScheduleAddDispatchProps & IScheduleAddOwnProps

const ScheduleAdd = (p_: ScheduleAddProps) => {
    const el = useRef<IAddScheduleRef | null>(null)
    const {
        devices,
        setDevices,
        deviceDelta,
        devicesCount,
        onToggleCatalogItem,
        onSidebarDevicesChange,
        onRemoveDevicesByIds,
        conflicts,
        save,
        cancel,
        broadcast,
        setBroadcast,
        advDuration,
        advContentLength,
        schedule,
        setSchedule,
        showCalendarAfterSave,
        contentDownloadDate,
        contentDownloadTime,
        setContentDownloadDate,
        setContentDownloadTime,
        orientationWarning,
        setOrientationWarning,
        onSelectFromFile,
        onClearSelectFromFile,
        selectFromFileMode,
        notSelectedDevices,
        setDevicesByAutoSelection,
        onCancelConfirm,
        loadMoreScheduleDevices,
        devicesNextPageToken,
        devicesPageLoading,
        devicesSearch,
        onDevicesSearchChange,
        groupUiDeviceIds,
    } = ScheduleAddMethods({ ...p_, el })

    const pathname = helpers.getPathname(p_.location.pathname)
    const query = getURLSearchParamsByLocation(p_.location)
    const onToolbarDevicesScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (!devicesNextPageToken || devicesPageLoading) return

        const { scrollHeight, clientHeight, scrollTop } = event.currentTarget
        if (scrollHeight - clientHeight - scrollTop < 40) {
            loadMoreScheduleDevices()
        }
    }
    const OrientationWarningModal = () => {
        return (
            <div
                className={cn(grid.colCenter, grid.p_microPlus, grid.pt_mini, {
                    [styles.dNoneModalSave]: !orientationWarning,
                    [styles.modalSaveApprove]: orientationWarning,
                })}
            >
                <div className={cn(grid.w80, grid.mb_md, grid.pt_micro)}>{translate('orientationWarning')}</div>
                <div className={cn(grid.pb_md, text.bold)}>{translate('save')}?</div>
                <div className={cn(grid.row, grid.justify, grid.mb_mini)}>
                    <Button mod="fill" className={cn(grid.mr_mini)} onClick={() => save(() => {}, true)}>
                        {translate('yes')}
                    </Button>
                    <Button animation={false} onClick={() => setOrientationWarning(false)}>
                        {translate('no')}
                    </Button>
                </div>
            </div>
        )
    }
    return (
        <Content
            toolbar={
                <AddScheduleToolbar
                    devices={devices}
                    devicesCount={devicesCount}
                    notSelectedDevices={notSelectedDevices}
                    conflicts={conflicts}
                    onChangeDevices={onSidebarDevicesChange}
                    onClearSelectFromFile={onClearSelectFromFile}
                    selectFromFileMode={selectFromFileMode}
                    onOptionsScroll={onToolbarDevicesScroll}
                    searchValue={query.scheduleId ? devicesSearch : undefined}
                    onSearchChange={query.scheduleId ? onDevicesSearchChange : undefined}
                    localSearch={!query.scheduleId}
                />
            }
        >
            <ScrollUp />
            <Stick enabled={true} top={46}>
                <div className={styles.buttons}>
                    <Button mod="fill" className={cn(grid.mr_mini)} onClick={cancel}>
                        {translate('cancel')}
                    </Button>
                    <Button mod={'withBorder'} onClick={() => save(() => {})}>
                        {query.scheduleId && pathname === routes.editSchedule.path
                            ? translate('update')
                            : translate('save')}
                    </Button>
                </div>
            </Stick>
            {orientationWarning && <OrientationWarningModal />}
            <AddSchedule
                addScheduleRef={el}
                broadcast={broadcast}
                onChangeBroadcast={(broadcast: Broadcast | null) => setBroadcast(broadcast)}
                advDuration={advDuration}
                advContentLength={advContentLength}
                devices={devices}
                useDeviceDelta={true}
                deviceDelta={deviceDelta}
                groupUiDeviceIds={groupUiDeviceIds}
                devicesCount={devicesCount}
                onToggleCatalogItem={onToggleCatalogItem}
                onRemoveDevicesByIds={onRemoveDevicesByIds}
                onChangeDevices={(devices: Device[], actionType?: 'auto') => {
                    if (actionType === 'auto') {
                        setDevicesByAutoSelection(devices)

                        return
                    }

                    setDevices(devices)
                }}
                onSelectFromFile={onSelectFromFile}
                schedule={schedule}
                changeLocationOnSave={showCalendarAfterSave}
                deferredDownload={{
                    date: contentDownloadDate,
                    time: contentDownloadTime,
                }}
                onChangeSchedule={(schedule: IEditableSchedule) => setSchedule(schedule)}
                onChangeDeferredDownload={(value, field) => {
                    if (field === 'contentDownloadDate') setContentDownloadDate(value)
                    if (field === 'contentDownloadTime') setContentDownloadTime(value)
                }}
                onCancelConfirm={onCancelConfirm}
            />
        </Content>
    )
}

const mapDispatchToProps = (dispatch: AppDispatch): IScheduleAddDispatchProps => {
    return {
        setTitle: (title) => dispatch(headActions.setTitle(title)),
    }
}

const exportScheduleAdd = checkAvailableModule('schedule/add')
    ? connect(
          (state: IRootState): IScheduleAddStateProps => ({
              selectedDate: state.schedule.selectedDate,
              filterList: state.schedule.filterList,
              view: state.user.data?.settings.view,
          }),
          mapDispatchToProps
      )(ScheduleAdd)
    : null

export default exportScheduleAdd
